import { useEffect, useMemo, useState } from "react";
import { History, Search, Upload, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TyreCell, fd } from "@/components/tyres/common";
import { ChangeDialog } from "@/components/tyres/ChangeDialog";
import { HistoryDialog } from "@/components/tyres/HistoryDialog";
import { TyreImportDialog } from "@/components/tyres/TyreImportDialog";
import { api } from "@/lib/api";

export const VehiclesTab = ({ sid, isAdmin, types, shops, tick, refresh }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [changing, setChanging] = useState(null);
  const [history, setHistory] = useState(null);
  const [imp, setImp] = useState(false);
  useEffect(() => { api.get("/tyres/vehicles", { params: { station_id: isAdmin ? sid : undefined } }).then((r) => setRows(r.data)); }, [sid, isAdmin, tick]);
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((v) => !t || [v.plate, v.model, v.current.front?.label, v.current.rear?.label].some((x) => (x || "").toLowerCase().includes(t)));
  }, [rows, q]);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, modello, gomma..." className="pl-9" data-testid="tyre-vehicle-search" />
      </div>
        <Button variant="outline" onClick={() => setImp(true)} data-testid="tyre-changes-import-button"><Upload className="mr-2 h-4 w-4" /> Importa storico cambi</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tyre-vehicle-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap [&_th]:px-3 [&_th]:py-2">
              <tr><th>Targa</th><th>Modello</th><th>Anteriori attuali</th><th>Posteriori attuali</th><th>Ultimo cambio</th><th /></tr>
            </thead>
            <tbody className="divide-y">
              {list.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50" data-testid={`tyre-vehicle-row-${v.id}`}>
                  <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold">{v.plate}</span></td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{v.model || "—"}</td>
                  <td className="px-3 py-2"><TyreCell t={v.current.front} /></td>
                  <td className="px-3 py-2"><TyreCell t={v.current.rear} /></td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{fd(v.last_change)}{v.changes > 0 && <span className="ml-1 text-slate-400">({v.changes})</span>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setHistory(v)} disabled={!v.changes} data-testid={`tyre-history-${v.id}`}><History className="mr-1 h-4 w-4" /> Storico</Button>
                    <Button size="sm" onClick={() => setChanging(v)} data-testid={`tyre-change-${v.id}`}><Wrench className="mr-1 h-4 w-4" /> Cambio gomme</Button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500" data-testid="tyre-vehicle-empty">Nessun furgone.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {changing && <ChangeDialog vehicle={changing} sid={sid} isAdmin={isAdmin} types={types} shops={shops} onClose={() => setChanging(null)} onSaved={() => { setChanging(null); refresh(); }} />}
      {imp && <TyreImportDialog kind="changes" sid={sid} isAdmin={isAdmin} onClose={() => setImp(false)} onDone={() => { setImp(false); refresh(); }} />}
      {history && <HistoryDialog vehicle={history} sid={sid} isAdmin={isAdmin} onClose={() => setHistory(null)} onChanged={refresh} />}
    </>
  );
};
